<template>
  <div class="tickets-list-container">

    <!-- Create Ticket Form (inline) -->
    <div v-if="showCreateTicketModal">
      <div class="page-header mb-4">
        <h1><i class="fas fa-plus"></i> Nouveau Ticket</h1>
        <b-button variant="outline-secondary" @click="showCreateTicketModal = false">
          <i class="fas fa-arrow-left"></i> Retour
        </b-button>
      </div>
      <b-card>
        <b-alert v-if="isAdmin" show variant="info" class="mb-3">
          <i class="fas fa-building"></i>
          <span class="mr-2">Client :</span>
          <b-form-select v-model="createTicketClientUid" size="sm" style="width:auto;display:inline-block">
            <b-form-select-option v-for="c in clientsList" :key="c.uid" :value="c.uid">{{ c.nom || c.uid }}</b-form-select-option>
          </b-form-select>
        </b-alert>
        <CreateTicketForm ref="ticketForm" :clientUid="createTicketClientUid || undefined" />
        <div class="mt-3 d-flex justify-content-end">
          <b-button variant="outline-secondary" class="mr-2" @click="showCreateTicketModal = false">Annuler</b-button>
          <b-button variant="primary" @click="createTicket" :disabled="submitting">
            <b-spinner v-if="submitting" small class="mr-1"></b-spinner>
            Créer le ticket
          </b-button>
        </div>
      </b-card>
    </div>

    <!-- Main List -->
    <template v-else>

    <!-- Header -->
    <div class="page-header mb-4">
      <h1>Tickets</h1>
      <div class="d-flex align-items-center">
        <b-button-group class="mr-3">
          <b-button 
            :variant="viewMode === 'cards' ? 'primary' : 'outline-primary'"
            @click="viewMode = 'cards'"
          >
            <i class="fas fa-th-large"></i> Cartes
          </b-button>
          <b-button 
            :variant="viewMode === 'table' ? 'primary' : 'outline-primary'"
            @click="viewMode = 'table'"
          >
            <i class="fas fa-table"></i> Tableau
          </b-button>
        </b-button-group>
        <b-button v-if="isAdmin" variant="outline-secondary" class="mr-2" @click="$router.push({ name: 'TacheMapping' })" v-b-tooltip.hover title="Correspondances tâches">
          <i class="fas fa-exchange-alt"></i>
        </b-button>
        <b-button variant="primary" @click="openCreateTicket">
          <i class="fas fa-plus"></i> Nouveau Ticket
        </b-button>
      </div>
    </div>

    <!-- Filters -->
    <b-card class="mb-4">
      <b-row>
        <b-col md="2">
          <b-form-group label="Rechercher:">
            <b-input-group>
              <b-form-input 
                v-model="filters.search" 
                placeholder="Rechercher..." 
                @input="onSearchInput"
              ></b-form-input>
              <b-input-group-append>
                <b-button 
                  variant="outline-secondary" 
                  @click="clearSearch"
                  v-if="filters.search"
                >
                  <i class="fas fa-times"></i>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col md="2">
          <b-form-group label="Statut:">
            <b-form-select v-model="filters.statut" @change="applyFilters">
              <b-form-select-option value="">Tous</b-form-select-option>
              <b-form-select-option value="ouvert">Ouvert</b-form-select-option>
              <b-form-select-option value="en cours">En cours</b-form-select-option>
              <b-form-select-option value="à tester">A tester</b-form-select-option>
              <b-form-select-option value="resolu">Résolu</b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>

        <b-col md="2">
          <b-form-group label="Priorité:">
            <b-form-select v-model="filters.priorite" @change="applyFilters">
              <b-form-select-option value="">Toutes</b-form-select-option>
              <b-form-select-option value="basse">Basse</b-form-select-option>
              <b-form-select-option value="moyenne">Moyenne</b-form-select-option>
              <b-form-select-option value="haute">Haute</b-form-select-option>
              <b-form-select-option value="urgente">Urgente</b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>

        <b-col md="2">
          <b-form-group label="Type:">
            <b-form-select v-model="filters.type" @change="applyFilters">
              <b-form-select-option value="">Tous</b-form-select-option>
              <b-form-select-option value="bug">Bug</b-form-select-option>
              <b-form-select-option value="feature">Feature</b-form-select-option>
              <b-form-select-option value="question">Question</b-form-select-option>
              <b-form-select-option value="autre">Autre</b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>

        <b-col md="2">
          <b-form-group label="Projet:">
            <b-form-select v-model="filters.projet" @change="applyFilters">
              <b-form-select-option value="">Tous</b-form-select-option>
              <b-form-select-option 
                v-for="project in filteredProjects" 
                :key="project" 
                :value="project"
              >
                {{ project }}
              </b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>

        <b-col md="2">
          <b-form-group label="Assigné à:">
            <b-form-select v-model="filters.assigned_to" @change="applyFilters">
              <b-form-select-option value="">Tous</b-form-select-option>
              <b-form-select-option value="_unassigned_">Non assigné</b-form-select-option>
              <optgroup label="SmartMyData">
                <b-form-select-option
                  v-for="user in smdUsersList"
                  :key="user.uid || 'support_smd'"
                  :value="user.uid"
                >
                  {{ user.text }}
                </b-form-select-option>
              </optgroup>
              <optgroup v-if="clientUsersList.length" label="Utilisateurs">
                <b-form-select-option
                  v-for="user in clientUsersList"
                  :key="user.uid"
                  :value="user.uid"
                >
                  {{ user.text }}
                </b-form-select-option>
              </optgroup>
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col v-if="isAdmin" md="2">
          <b-form-group label="Client:">
            <b-form-select v-model="filters.client_uid" @change="applyFilters">
              <b-form-select-option value="_all_">Tous les clients</b-form-select-option>
              <b-form-select-option 
                v-for="client in clientsList" 
                :key="client.uid" 
                :value="client.uid"
              >
                {{ client.nom }}
              </b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>

        <b-col md="2">
          <b-form-group label="Tri:">
            <b-form-select v-model="filters.sort" @change="applyFilters">
              <b-form-select-option value="date">Date</b-form-select-option>
              <b-form-select-option value="priorite">Priorité</b-form-select-option>
              <b-form-select-option value="statut">Statut</b-form-select-option>
              <b-form-select-option value="titre">Titre</b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>

        <b-col md="2">
          <b-form-group label="Ordre:">
            <b-form-select v-model="filters.order" @change="applyFilters">
              <b-form-select-option value="DESC">↓ Desc</b-form-select-option>
              <b-form-select-option value="ASC">↑ Asc</b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>

        <b-col md="2">
          <b-form-group>
            <template #label>
              <span style="visibility: hidden;">.</span>
            </template>
            <b-button variant="outline-secondary" @click="resetFilters" block>
              <i class="fas fa-redo"></i> Réinitialiser
            </b-button>
          </b-form-group>
        </b-col>

        <b-col md="2">
          <b-form-group label="Vue rapide :">
            <div class="d-flex" style="gap: 6px;">
              <b-button
                :variant="filters.only_followed ? 'warning' : 'outline-warning'"
                size="sm"
                class="flex-fill position-relative"
                v-b-tooltip.hover
                :title="(filters.only_followed ? 'Activé : ' : '') + 'Afficher uniquement les tickets que je suis'"
                @click="filters.only_followed = !filters.only_followed; applyFilters()"
              >
                <i class="fas fa-bell"></i>
                <i v-if="filters.only_followed" class="fas fa-check-circle quick-filter-check"></i>
              </b-button>
              <b-button
                :variant="filters.only_unread ? 'primary' : 'outline-primary'"
                size="sm"
                class="flex-fill position-relative"
                v-b-tooltip.hover
                :title="(filters.only_unread ? 'Activé : ' : '') + 'Afficher uniquement les tickets non lus'"
                @click="filters.only_unread = !filters.only_unread; applyFilters()"
              >
                <i class="fas fa-envelope"></i>
                <i v-if="filters.only_unread" class="fas fa-check-circle quick-filter-check"></i>
              </b-button>
              <b-button
                :variant="filters.hide_sub_tickets ? 'dark' : 'outline-secondary'"
                size="sm"
                class="flex-fill position-relative"
                v-b-tooltip.hover
                :title="filters.hide_sub_tickets ? 'Activé : les sous-tickets sont masqués' : 'Masquer les sous-tickets'"
                @click="filters.hide_sub_tickets = !filters.hide_sub_tickets; applyFilters()"
              >
                <i :class="filters.hide_sub_tickets ? 'fas fa-eye-slash' : 'fas fa-sitemap'"></i>
                <i v-if="filters.hide_sub_tickets" class="fas fa-check-circle quick-filter-check"></i>
              </b-button>
            </div>
          </b-form-group>
        </b-col>

        <b-col md="2">
          <b-form-group label="Actions :">
            <b-button variant="outline-secondary" @click="markAllSeen" block size="sm">
              <i class="fas fa-check-double"></i> Tout marquer comme lu
            </b-button>
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <!-- Loading -->
    <div v-if="loading" class="text-center my-5">
      <b-spinner label="Chargement..."></b-spinner>
      <p class="mt-2">Chargement...</p>
    </div>

    <!-- Tickets List - Cards View -->
    <b-row v-else-if="viewMode === 'cards'">
      <b-col
        v-for="ticket in tickets"
        :key="ticket.id"
        md="6"
        lg="4"
        class="mb-4"
      >
        <b-card
          class="ticket-card h-100"
          :class="'border-left-' + ticket.priorite"
          @click="viewTicket(ticket.id)"
          style="cursor: pointer;"
        >
          <template #header>
            <div class="d-flex justify-content-between align-items-center">
              <strong class="text-muted">#{{ ticket.id }}</strong>
              <b-badge :variant="getStatusVariant(ticket.statut)">
                {{ ticket.statut }}
              </b-badge>
            </div>
          </template>

          <b-card-title :class="{ 'ticket-unread': ticket.has_new }">
            {{ ticket.titre }}
          </b-card-title>

          <div v-if="ticket.parent_ticket" class="mb-2">
            <b-badge variant="info">
              <i class="fas fa-level-up-alt"></i> Sous-ticket
            </b-badge>
          </div>

          <div v-if="isAdmin && ticket.client_name" class="mb-2">
            <b-badge variant="dark">
              <i class="fas fa-building"></i> {{ ticket.client_name }}
            </b-badge>
          </div>

          <div class="mb-3">
            <b-badge variant="secondary" class="mr-2">
              <i class="fas fa-tag"></i> {{ ticket.type }}
            </b-badge>
            <b-badge :variant="getPriorityVariant(ticket.priorite)">
              <i class="fas fa-exclamation-circle"></i> {{ ticket.priorite }}
            </b-badge>
          </div>

          <div v-if="ticket.projet" class="mb-2">
            <small class="text-muted">
              <i class="fas fa-folder"></i> {{ ticket.projet }}
            </small>
          </div>

          <div v-if="ticket.assigned_to" class="mb-2">
            <small class="text-muted">
              <i class="fas fa-user"></i> {{ getAssignedUserName(ticket.assigned_to) }}
            </small>
          </div>

          <template #footer>
            <small class="text-muted">
              <i class="fas fa-clock"></i> {{ formatDate(ticket.date) }}
            </small>
          </template>
        </b-card>
      </b-col>
    </b-row>

    <!-- Tickets List - Table View -->
    <b-card v-else-if="viewMode === 'table'">
      <b-table
        :items="tickets"
        :fields="computedTableFields"
        striped
        hover
        responsive
        show-empty
        empty-text="Aucun ticket trouvé"
        @row-clicked="viewTicket"
        class="tickets-table mb-0"
      >
        <template #cell(id)="data">
          <strong class="text-primary">#{{ data.value }}</strong>
        </template>

        <template #cell(titre)="data">
          <div class="ticket-title" :class="{ 'ticket-unread': data.item.has_new }">
            <b-badge v-if="data.item.parent_ticket" variant="info" class="mr-1" v-b-tooltip.hover title="Sous-ticket">
              <i class="fas fa-level-up-alt"></i>
            </b-badge>
            {{ truncateText(data.value, 50) }}
          </div>
        </template>

        <template #cell(statut)="data">
          <b-badge :variant="getStatusVariant(data.value)">
            {{ data.value }}
          </b-badge>
        </template>

        <template #cell(priorite)="data">
          <b-badge :variant="getPriorityVariant(data.value)">
            <i class="fas fa-exclamation-circle"></i> {{ data.value }}
          </b-badge>
        </template>

        <template #cell(type)="data">
          <b-badge variant="secondary">
            <i class="fas fa-tag"></i> {{ data.value }}
          </b-badge>
        </template>

        <template #cell(projet)="data">
          <span v-if="data.value" class="text-muted">
            <i class="fas fa-folder"></i> {{ data.value }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell(assigned_to)="data">
          <span v-if="data.value" class="text-muted">
            <i class="fas fa-user"></i> {{ getAssignedUserName(data.value) }}
          </span>
          <span v-else class="text-muted">Non assigné</span>
        </template>

        <template #cell(client_name)="data">
          <b-badge v-if="data.value" variant="dark">
            <i class="fas fa-building"></i> {{ data.value }}
          </b-badge>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell(date)="data">
          <small class="text-muted">
            <i class="fas fa-clock"></i> {{ formatDate(data.value) }}
          </small>
        </template>
      </b-table>
    </b-card>

    <!-- Pagination -->
    <b-pagination
      v-if="pagination.pages > 1"
      v-model="pagination.page"
      :total-rows="pagination.total"
      :per-page="pagination.limit"
      @change="changePage"
      align="center"
      class="mt-4"
    ></b-pagination>

    </template><!-- end v-else main list -->
  </div>
</template>

<script>
import ticketService from '../services/tickets';
import clientService from '../services/client';
import CreateTicketForm from '../components/CreateTicketForm.vue';

export default {
  name: 'TicketsList',
  components: {
    CreateTicketForm
  },
  data() {
    return {
      tickets: [],
      loading: false,
      submitting: false,
      showCreateTicketModal: false,
      createTicketClientUid: '',
      viewMode: 'cards',
      tableFields: [
        { key: 'id', label: '#', sortable: true, thStyle: { width: '80px' } },
        { key: 'titre', label: 'Titre', sortable: true },
        { key: 'statut', label: 'Statut', sortable: true, thStyle: { width: '120px' } },
        { key: 'priorite', label: 'Priorité', sortable: true, thStyle: { width: '130px' } },
        { key: 'type', label: 'Type', sortable: true, thStyle: { width: '110px' } },
        { key: 'projet', label: 'Projet', sortable: true, thStyle: { width: '150px' } },
        { key: 'assigned_to', label: 'Assigné à', sortable: true, thStyle: { width: '150px' } },
        { key: 'date', label: 'Date', sortable: true, thStyle: { width: '180px' } }
      ],
      filters: {
        statut: '',
        priorite: '',
        type: '',
        projet: '',
        assigned_to: '',
        client_uid: '',
        allClients: false,
        search: '',
        sort: 'date',
        order: 'DESC',
        only_followed: false,
        only_unread: false,
        hide_sub_tickets: false
      },
      pagination: {
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
      },
      newTicket: {
        titre: '',
        type: 'bug',
        priorite: 'moyenne',
        projet: '',
        demande: '',
        urls: '',
        statut: 'ouvert',
        files: []
      },
      usersList: [],
      projectsList: [], // Liste des projets filtrée
      allProjectsList: [], // Liste complète de tous les projets (pour admin)
      clientsList: [],
      searchTimeout: null,
      isRestoringState: false // Flag pour éviter la réinitialisation pendant la restauration
    };
  },
  computed: {
    isAdmin() {
      const user = this.$store.state.user;
      return !!(user && user.details && user.details.client_uid);
    },
    smdUsersList() {
      return this.usersList.filter(u => u.uid === 'support_smd' || u.is_admin);
    },
    clientUsersList() {
      return this.usersList.filter(u => u.uid !== 'support_smd' && !u.is_admin);
    },
    currentClientName() {
      if (!this.isAdmin) return '';
      if (!this.filters.client_uid || this.filters.client_uid === '_all_') return 'Tous les clients (non défini)';
      const client = this.clientsList.find(c => c.uid === this.filters.client_uid);
      return client ? (client.nom || client.uid) : this.filters.client_uid;
    },
    filteredProjects() {
      // Si pas admin, retourner la liste des projets du client
      if (!this.isAdmin) {
        return this.projectsList;
      }
      
      // Si admin et "Tous les clients" sélectionné
      if (this.filters.client_uid === '_all_') {
        return this.allProjectsList;
      }
      
      // Si admin et client spécifique, retourner les projets filtrés
      return this.projectsList;
    },
    computedTableFields() {
      let fields = [
        { key: 'id', label: '#', thStyle: { width: '80px' } },
        { key: 'titre', label: 'Titre' },
        { key: 'statut', label: 'Statut', thStyle: { width: '120px' } },
        { key: 'priorite', label: 'Priorité', thStyle: { width: '130px' } },
        { key: 'type', label: 'Type', thStyle: { width: '110px' } },
        { key: 'projet', label: 'Projet', thStyle: { width: '150px' } },
        { key: 'assigned_to', label: 'Assigné à', thStyle: { width: '150px' } }
      ];
      
      if (this.isAdmin) {
        fields.splice(2, 0, { key: 'client_name', label: 'Client', thStyle: { width: '180px' } });
      }
      
      fields.push({ key: 'date', label: 'Date', thStyle: { width: '180px' } });
      
      return fields;
    }
  },
  watch: {
    viewMode(newValue) {
      // Sauvegarder le choix de vue dans localStorage
      localStorage.setItem('ticketsViewMode', newValue);
    },
    'filters.client_uid': async function(newValue, oldValue) {
      // Ne pas réinitialiser pendant la restauration de l'état
      if (this.isRestoringState) {
        return;
      }
      
      // Recharger les projets quand le client change
      if (this.isAdmin && newValue !== oldValue) {
        await this.loadProjects();
        // Réinitialiser le filtre projet si le client change
        this.filters.projet = '';
      }
    }
  },
  async mounted() {
    // Charger le mode d'affichage depuis localStorage
    const savedViewMode = localStorage.getItem('ticketsViewMode');
    if (savedViewMode && (savedViewMode === 'cards' || savedViewMode === 'table')) {
      this.viewMode = savedViewMode;
    }
    
    // Charger les utilisateurs et clients d'abord
    this.loadUsers();
    if (this.isAdmin) {
      this.loadClients();
    }
    
    // Charger les filtres depuis sessionStorage uniquement si on revient d'un ticket
    const savedState = sessionStorage.getItem('ticketsFilters');
    
    if (savedState) {
      try {
        this.isRestoringState = true; // Désactiver les watchers pendant la restauration
        const state = JSON.parse(savedState);
        
        // D'abord charger les projets avec le bon client_uid si nécessaire
        if (state.filters && state.filters.client_uid) {
          this.filters.client_uid = state.filters.client_uid;
        }
        await this.loadProjects();
        
        // Ensuite restaurer tous les filtres (y compris le projet)
        this.filters = { ...this.filters, ...state.filters };
        this.pagination.page = state.page || 1;
        
        // Nettoyer immédiatement après restauration
        sessionStorage.removeItem('ticketsFilters');
        
        // Forcer la réactivité pour le select projet
        this.$nextTick(() => {
          // Le DOM est mis à jour, le select devrait afficher la bonne valeur
        });
      } catch (e) {
        console.error('Erreur lors de la restauration:', e);
        // Si erreur, charger depuis l'URL
        this.loadFiltersFromUrl();
        await this.loadProjects();
      } finally {
        this.isRestoringState = false; // Réactiver les watchers
      }
    } else {
      this.loadFiltersFromUrl();
      await this.loadProjects();
    }
    
    this.loadTickets();
  },
  methods: {
    loadFiltersFromUrl() {
      const query = this.$route.query;
      
      // Charger les filtres depuis l'URL ou utiliser les valeurs par défaut
      this.filters.statut = query.statut || '';
      this.filters.priorite = query.priorite || '';
      this.filters.type = query.type || '';
      this.filters.projet = query.projet || '';
      this.filters.assigned_to = query.assigned_to || '';
      this.filters.search = query.search || '';
      this.filters.sort = query.sort || 'date';
      this.filters.order = query.order || 'DESC';
      
      // Charger la pagination
      if (query.page) {
        this.pagination.page = parseInt(query.page);
      }
      
      // Gérer le filtre client pour les admins
      if (this.isAdmin) {
        if (query.client_uid) {
          this.filters.client_uid = query.client_uid;
        } else {
          // Initialiser avec le client actuel si aucun filtre dans l'URL
          const user = this.$store.state.user;
          if (user && user.client_uid) {
            this.filters.client_uid = user.client_uid;
          }
        }
      }
    },

    updateUrlWithFilters() {
      const query = {
        page: this.pagination.page
      };
      
      // Ajouter les filtres non vides à l'URL
      if (this.filters.statut) query.statut = this.filters.statut;
      if (this.filters.priorite) query.priorite = this.filters.priorite;
      if (this.filters.type) query.type = this.filters.type;
      if (this.filters.projet) query.projet = this.filters.projet;
      if (this.filters.assigned_to) query.assigned_to = this.filters.assigned_to;
      if (this.filters.search) query.search = this.filters.search;
      if (this.filters.sort && this.filters.sort !== 'date') query.sort = this.filters.sort;
      if (this.filters.order && this.filters.order !== 'DESC') query.order = this.filters.order;
      if (this.isAdmin && this.filters.client_uid) query.client_uid = this.filters.client_uid;
      
      // Mettre à jour l'URL sans recharger la page
      this.$router.replace({ query }).catch(() => {});
    },

    async loadUsers() {
      try {
        const data = await ticketService.listUsers();
        this.usersList = data.users || [];
      } catch (error) {
        console.error('Error loading users:', error);
        this.usersList = [];
      }
    },

    async loadProjects() {
      try {
        // Si admin, charger les projets selon le filtre client
        if (this.isAdmin) {
          if (this.filters.client_uid === '_all_') {
            // Charger tous les projets de tous les clients
            const data = await ticketService.listProjects('_all_');
            this.projectsList = data.projects || [];
            this.allProjectsList = data.projects || [];
          } else if (this.filters.client_uid) {
            // Charger les projets du client spécifique
            const data = await ticketService.listProjects(this.filters.client_uid);
            this.projectsList = data.projects || [];
          } else {
            // Pas de client sélectionné, charger les projets du client de l'admin
            const data = await ticketService.listProjects();
            this.projectsList = data.projects || [];
          }
        } else {
          // Non-admin, charger uniquement les projets de son client
          const data = await ticketService.listProjects();
          this.projectsList = data.projects || [];
        }
      } catch (error) {
        console.error('Error loading projects:', error);
        this.projectsList = [];
        this.allProjectsList = [];
      }
    },

    async loadClients() {
      try {
        const clients = await clientService.getClients();
        this.clientsList = (clients || [])
          .filter(client => !client.archive) // Exclure les clients archivés
          .sort((a, b) => {
            const nameA = (a.nom || '').toLowerCase();
            const nameB = (b.nom || '').toLowerCase();
            return nameA.localeCompare(nameB);
          });
      } catch (error) {
        console.error('Error loading clients:', error);
        this.clientsList = [];
      }
    },

    async loadTickets() {
      this.loading = true;
      try {
        const params = {
          statut: this.filters.statut,
          priorite: this.filters.priorite,
          type: this.filters.type,
          projet: this.filters.projet,
          assigned_to: this.filters.assigned_to,
          search: this.filters.search,
          sort: this.filters.sort,
          order: this.filters.order,
          page: this.pagination.page,
          limit: this.pagination.limit,
          only_followed: this.filters.only_followed,
          only_unread: this.filters.only_unread,
          hide_sub_tickets: this.filters.hide_sub_tickets
        };
        
        // Gestion du filtre client
        if (this.filters.client_uid === '_all_') {
          // Tous les clients
          params.allClients = true;
        } else if (this.filters.client_uid) {
          // Client spécifique
          params.client_uid = this.filters.client_uid;
        }
        
        const data = await ticketService.listTickets(params);

        this.tickets = data.tickets;
        this.pagination = data.pagination;
      } catch (error) {
        console.error('Error loading tickets:', error);
        this.$notify({
          group: 'info',
          title: 'Erreur',
          text: 'Erreur lors du chargement des tickets',
          type: 'error'
        });
      } finally {
        this.loading = false;
      }
    },

    openCreateTicket() {
      // Default to currently filtered client only if it's a real client in the list
      if (this.isAdmin) {
        const validClient = this.clientsList.find(c => c.uid === this.filters.client_uid);
        this.createTicketClientUid = validClient
          ? validClient.uid
          : (this.clientsList.length ? this.clientsList[0].uid : '');
      }
      this.showCreateTicketModal = true;
    },

    async createTicket() {
      this.submitting = true;
      try {
        const formData = this.$refs.ticketForm.getData();
        const files = formData.files;
        delete formData.files;

        // Admin can choose client
        if (this.isAdmin && this.createTicketClientUid) {
          formData.client_uid = this.createTicketClientUid;
        }

        const data = await ticketService.createTicket(formData);
        const ticketId = data.ticket_id;

        if (files && files.length > 0) {
          await ticketService.uploadTicketFiles(ticketId, files);
        }

        this.$notify({
          group: 'info',
          title: 'Succès',
          text: 'Ticket créé avec succès',
          type: 'success'
        });
        this.resetNewTicket();
        this.showCreateTicketModal = false;
        this.$router.push({ name: 'TicketDetail', params: { id: ticketId } });
      } catch (error) {
        console.error('Error creating ticket:', error);
        this.$notify({
          group: 'info',
          title: 'Erreur',
          text: error.response?.data?.error || 'Erreur lors de la création du ticket',
          type: 'error'
        });
      } finally {
        this.submitting = false;
      }
    },

    viewTicket(ticketIdOrObj) {
      const ticketId = typeof ticketIdOrObj === 'object' ? ticketIdOrObj.id : ticketIdOrObj;
      
      // Sauvegarder l'état actuel dans sessionStorage pour le retour
      sessionStorage.setItem('ticketsFilters', JSON.stringify({
        filters: this.filters,
        page: this.pagination.page
      }));
      
      this.$router.push({ name: 'TicketDetail', params: { id: ticketId } });
    },

    changePage(page) {
      this.pagination.page = page;
      this.updateUrlWithFilters();
      this.loadTickets();
    },

    applyFilters() {
      this.pagination.page = 1;
      this.updateUrlWithFilters();
      this.loadTickets();
    },

    onSearchInput() {
      // Débouncer la recherche pour ne pas surcharger l'API
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.applyFilters();
      }, 500); // Attendre 500ms après la dernière frappe
    },

    clearSearch() {
      this.filters.search = '';
      this.applyFilters();
    },

    resetFilters() {
      this.filters.statut = '';
      this.filters.priorite = '';
      this.filters.type = '';
      this.filters.projet = '';
      this.filters.assigned_to = '';
      this.filters.search = '';
      this.filters.sort = 'date';
      this.filters.order = 'DESC';
      this.filters.only_followed = false;
      this.filters.only_unread = false;
      this.filters.hide_sub_tickets = false;
      
      // Réinitialiser client_uid avec le client actuel si admin
      if (this.isAdmin) {
        const user = this.$store.state.user;
        if (user && user.client_uid) {
          this.filters.client_uid = user.client_uid;
        }
      } else {
        this.filters.client_uid = '';
      }
      
      // Nettoyer sessionStorage
      sessionStorage.removeItem('ticketsFilters');
      
      this.applyFilters();
    },

    resetNewTicket() {
      if (this.$refs.ticketForm) {
        this.$refs.ticketForm.reset();
      }
    },

    async markAllSeen() {
      try {
        await ticketService.markAllSeen();
        await this.loadTickets();
        this.$notify({ group: 'info', title: 'Succès', text: 'Tous les tickets ont été marqués comme lus', type: 'success' });
      } catch (error) {
        console.error('Error marking all as seen:', error);
        this.$notify({ group: 'info', title: 'Erreur', text: 'Erreur lors de la mise à jour', type: 'error' });
      }
    },

    getStatusVariant(statut) {
      const variants = {
        'ouvert': 'primary',
        'en_cours': 'warning',
        'resolu': 'success',
        'ferme': 'secondary'
      };
      return variants[statut] || 'secondary';
    },

    getPriorityVariant(priorite) {
      const variants = {
        'urgente': 'danger',
        'haute': 'warning',
        'moyenne': 'info',
        'basse': 'success'
      };
      return variants[priorite] || 'secondary';
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    truncateText(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },

    getAssignedUserName(userId) {
      if (!userId) return '';
      const user = this.usersList.find(u => u.uid === userId);
      return user ? user.text : userId;
    }
  }
};
</script>

<style scoped>
.tickets-list-container {
  padding: 20px;
}

.quick-filter-check {
  position: absolute;
  top: -6px;
  right: -6px;
  font-size: 12px;
  color: #28a745;
  background: #fff;
  border-radius: 50%;
  line-height: 1;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-header h1 {
  margin: 0;
  font-size: 28px;
}

.ticket-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
  transition: all 0.2s;
}

.border-left-urgente {
  border-left: 4px solid #dc3545 !important;
}

.border-left-haute {
  border-left: 4px solid #ffc107 !important;
}

.border-left-moyenne {
  border-left: 4px solid #17a2b8 !important;
}

.border-left-basse {
  border-left: 4px solid #28a745 !important;
}

.ticket-preview-html {
  color: #555;
  line-height: 1.5;
  overflow: hidden;
}

.ticket-preview-html >>> p {
  margin-bottom: 0.5rem;
}

.ticket-preview-html >>> img {
  max-width: 100%;
  height: auto;
}

.ticket-preview-html >>> a {
  color: #007bff;
}

.tickets-table {
  cursor: pointer;
}

.tickets-table >>> tbody tr:hover {
  background-color: #f8f9fa;
}

.ticket-unread {
  font-weight: 700;
}

.ticket-title.ticket-unread {
  font-weight: 700;
}

.ticket-unread-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #fd7e14;
  margin-right: 5px;
  vertical-align: middle;
  flex-shrink: 0;
}

.ticket-title {
  font-weight: 500;
  color: #333;
}

.tickets-table >>> td {
  vertical-align: middle;
}
</style>
